/**
 * CORS Admin API
 *
 * Endpoints administrativos para visualizar e gerenciar o CORS dinâmico.
 * Somente superusers.
 */

import { apiClient } from './api'

// Types

export interface CorsStatus {
  /**
   * `null` = a zona da plataforma ainda não foi resolvida (#961). Não é um vazio
   * a preencher com um default: nesse estado o backend não autoriza origem
   * nenhuma por wildcard e não trata host nenhum como plataforma. A tela precisa
   * dizer isso — antes o campo era obrigatório e o painel mostrava a zona
   * histórica com o selo "ativo" enquanto o login do ambiente estava recusado.
   */
  base_domain: string | null
  /**
   * Opcional de propósito: Fly (backend) e Pages (frontend) deployam separados,
   * então na janela em que o front novo fala com o backend ANTIGO este campo não
   * vem. Declarar `boolean` obrigatório faria o `??` do consumidor parecer
   * redundante — e removê-lo devolve o falso "não resolvido" naquela janela.
   */
  base_domain_resolved?: boolean
  base_domain_source?: string | null
  base_domain_resolved_at?: string | null
  base_domain_last_error?: string | null
  wildcard_pattern: string | null
  platform_subdomains: string[]
  total_portal_domains: number
  total_custom_domains: number
  redis_cache_key: string
  redis_cache_populated: boolean
  /**
   * A régua de origens locais (#1089): `localhost`/`<slug>.localhost` e `http://` da zona
   * valem só em development/staging (ENVIRONMENT) ou com DEBUG. Antes o painel lia DEBUG
   * e dizia "bloqueado" em produção enquanto o CORS aceitava `localhost`.
   */
  localhost_allowed: boolean
  /** Opcionais: na janela em que o front novo fala com o backend antigo, não vêm. */
  insecure_http_allowed?: boolean
  local_origins_reason?: string | null
  environment?: string | null
  /**
   * Origens EXATAS de `CORS_ORIGINS` (#991) — a escotilha de bootstrap. Ficam no
   * painel porque, sem zona resolvida, são a única porta que sobra: o operador
   * precisa saber qual host declarar ANTES de perder o acesso ao Admin. `"*"`
   * não aparece — fora de DEBUG ele não autoriza nada.
   *
   * Opcional: Fly e Pages deployam separados, e na janela em que o front novo
   * fala com o backend antigo o campo não vem.
   */
  bootstrap_allowed_origins?: string[]
  /** As duas fontes do domínio-base, com o estado de divergência (#991). */
  base_domain_sources?: PlatformDomainSources | null
}

/** Espelha `PlatformDomainSources` de `cloudflareApi` — o mesmo censo. */
export interface PlatformDomainSources {
  ec_app_zone: string | null
  ec_app_found: boolean
  ec_app_error: string | null
  cloudflare_zone: string | null
  cloudflare_config_id: string | null
  cloudflare_config_name: string | null
  cloudflare_default_zone_id: string | null
  cloudflare_error: string | null
  active_configs: number
  diverging: boolean
}

export interface AdminPortalDomain {
  id: string
  domain: string
  subdomain: string
  root_domain: string
  reseller_name: string
  dns_status: string
  ssl_status: string
  is_active: boolean
  is_primary: boolean
  created_at: string
}

export interface CorsTestResult {
  origin: string
  allowed: boolean
  reason: string
}

export interface CacheReloadResult {
  message: string
  custom_domains_loaded: number
  /** `false` = o Redis NÃO foi atualizado (não confundir com "0 domínios"). */
  origins_reloaded?: boolean
  base_domain?: string | null
  base_domain_resolved?: boolean
}

// API

const corsAdminApi = {
  getCorsStatus: (): Promise<CorsStatus> =>
    apiClient.get('/api/v1/portal-config/admin/cors-status'),

  getAllDomains: (search?: string, status?: string): Promise<AdminPortalDomain[]> => {
    const params: Record<string, string> = {}
    if (search) params.search = search
    if (status) params.status = status
    return apiClient.get('/api/v1/portal-config/admin/domains', { params })
  },

  reloadCache: (): Promise<CacheReloadResult> =>
    apiClient.post('/api/v1/portal-config/admin/cors-cache/reload'),

  testOrigin: (origin: string): Promise<CorsTestResult> =>
    apiClient.post('/api/v1/portal-config/admin/cors-test', { origin }),
}

export default corsAdminApi
